import { useState } from 'react';
import { faqItems, faqCategories } from '@/mocks/faq';

export default function FaqAccordion() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [openId, setOpenId] = useState<string | null>('faq-1');

  const filtered = activeCategory === 'all'
    ? faqItems
    : faqItems.filter((f) => f.category === activeCategory);

  const toggle = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  const categoryColors: Record<string, string> = {
    fee: 'bg-amber-50 text-amber-700 border-amber-200',
    budget: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    timeline: 'bg-sky-50 text-sky-700 border-sky-200',
    renovation: 'bg-rose-50 text-rose-700 border-rose-200',
    process: 'bg-violet-50 text-violet-700 border-violet-200',
  };

  const categoryLabels: Record<string, string> = {
    fee: '收費方式',
    budget: '裝修預算',
    timeline: '工期時程',
    renovation: '老店翻新',
    process: '合作流程',
  };

  return (
    <section className="py-20 md:py-28 bg-[#fafaf8]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">

          {/* Left: Category Filter (sticky) */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="lg:sticky lg:top-28">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#b8956a] mb-5">依類別瀏覽</p>
              <nav className="flex flex-row lg:flex-col gap-2 flex-wrap">
                {faqCategories.map((cat) => {
                  const isActive = activeCategory === cat.key;
                  return (
                    <button
                      key={cat.key}
                      onClick={() => { setActiveCategory(cat.key); setOpenId(null); }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap text-left w-full ${
                        isActive
                          ? 'bg-[#1a1a1a] text-white'
                          : 'bg-white text-[#3a3a3a] hover:bg-[#f0ede8] border border-[#e2ddd8]'
                      }`}
                    >
                      <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0`}>
                        <i className={`${cat.icon} text-base`}></i>
                      </div>
                      <span>{cat.label}</span>
                      <span className={`ml-auto text-xs px-1.5 py-0.5 rounded-full ${
                        isActive ? 'bg-white/20 text-white' : 'bg-[#f0ede8] text-[#8a7a6a]'
                      }`}>
                        {cat.key === 'all'
                          ? faqItems.length
                          : faqItems.filter((f) => f.category === cat.key).length}
                      </span>
                    </button>
                  );
                })}
              </nav>

              {/* Contact tip */}
              <div className="mt-8 p-5 bg-[#f0ede8] rounded-xl border border-[#e2ddd8]">
                <p className="text-[#5a5a5a] text-sm leading-relaxed mb-4">
                  沒找到你想問的問題？直接聯繫我們，我們會在 24 小時內回覆。
                </p>
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 text-[#b8956a] text-sm font-medium hover:text-[#a07d58] transition-colors cursor-pointer"
                >
                  前往預約諮詢
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </aside>

          {/* Right: FAQ List */}
          <div className="flex-1 min-w-0">
            {/* Count */}
            <div className="flex items-center justify-between mb-8">
              <p className="text-[#8a7a6a] text-sm">
                共 <strong className="text-[#1a1a1a]">{filtered.length}</strong> 個問題
              </p>
              {activeCategory !== 'all' && (
                <button
                  onClick={() => setActiveCategory('all')}
                  className="text-xs text-[#8a7a6a] hover:text-[#b8956a] flex items-center gap-1 cursor-pointer transition-colors"
                >
                  <i className="ri-close-circle-line"></i>
                  清除篩選
                </button>
              )}
            </div>

            <div className="flex flex-col gap-4">
              {filtered.map((item, idx) => {
                const isOpen = openId === item.id;
                const tagColor = categoryColors[item.category] ?? 'bg-gray-50 text-gray-600 border-gray-200';
                const tagLabel = categoryLabels[item.category] ?? item.category;

                return (
                  <div
                    key={item.id}
                    className={`rounded-xl border transition-all duration-300 overflow-hidden ${
                      isOpen ? 'border-[#b8956a]/40 bg-white' : 'border-[#e2ddd8] bg-white hover:border-[#c8a87a]/50'
                    }`}
                  >
                    <button
                      className="w-full flex items-start gap-4 px-6 py-5 text-left cursor-pointer"
                      onClick={() => toggle(item.id)}
                    >
                      {/* Number */}
                      <span
                        className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold mt-0.5 ${
                          isOpen ? 'bg-[#b8956a] text-white' : 'bg-[#f0ede8] text-[#b8956a]'
                        }`}
                      >
                        {String(idx + 1).padStart(2, '0')}
                      </span>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${tagColor}`}>
                            {tagLabel}
                          </span>
                        </div>
                        <span
                          className={`font-semibold leading-snug transition-colors ${
                            isOpen ? 'text-[#b8956a]' : 'text-[#1a1a1a]'
                          }`}
                          style={{ fontSize: '1.0625rem' }}
                        >
                          {item.question}
                        </span>
                      </div>

                      <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-300 ${
                        isOpen ? 'bg-[#b8956a] rotate-180' : 'bg-[#f0ede8]'
                      }`}>
                        <i className={`text-sm transition-colors ${isOpen ? 'ri-arrow-up-s-line text-white' : 'ri-arrow-down-s-line text-[#b8956a]'}`}></i>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="px-6 pb-6 pt-0">
                        <div className="ml-11 pl-4 border-l-2 border-[#b8956a]/30">
                          {item.answer.split('\n').map((line, i) => (
                            line.trim() === '' ? (
                              <div key={i} className="h-3"></div>
                            ) : (
                              <p key={i} className="text-[#4a4a4a] leading-[1.85] text-[0.9375rem]">
                                {line}
                              </p>
                            )
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
